import { useState, useEffect } from "react";
import Footer from "../../components/feature/Footer";

const TWITCH_PARENT = window.location.hostname || "localhost";
const YOUTUBE_API_KEY = "AIzaSyCv5hCJplm6-DkK41hihSjUBK-T7sfp-yw";

const STREAMERS = [
  {
    id: "bobbitto",
    displayName: "Bobbitto",
    twitch: "ItsBobbitto",
    youtubeHandle: "ItsBobbitto",
    youtubeChannelId: "UC_md61S_Eem7k4Evf1aV8dw",
  },
  {
    id: "chlimro",
    displayName: "Chlimro",
    twitch: "ChlimroGames",
    youtubeHandle: "ChlimroGames",
    youtubeChannelId: "UCfUA3anmp7q0KamqHY8PhNA", // fetched dynamically via handle
  },
];

interface TwitchStream { isLive: boolean; }
interface YouTubeLive { isLive: boolean; videoId?: string; title?: string; }
interface ChannelStats { subscriberCount?: string; viewCount?: string; }

interface StreamerData {
  twitchStream: TwitchStream;
  twitchFollowers: number | null;
  youtubeLive: YouTubeLive;
  channelStats: ChannelStats | null;
}

function parseFormattedCount(raw: string | number | undefined): string {
  if (raw === undefined || raw === null) return "—";
  const num = typeof raw === "string" ? Number(raw.replace(/,/g, "")) : raw;
  if (isNaN(num)) return String(raw);
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return num.toLocaleString();
}

async function fetchStreamerData(streamer: typeof STREAMERS[0]): Promise<StreamerData> {
  const result: StreamerData = {
    twitchStream: { isLive: false },
    twitchFollowers: null,
    youtubeLive: { isLive: false },
    channelStats: null,
  };

  // Twitch live check
  try {
    const res = await fetch(`https://api.decapi.me/twitch/uptime/${streamer.twitch}`);
    const text = await res.text();
    result.twitchStream = { isLive: text.trim().toLowerCase() !== "offline" };
  } catch {}

  // Twitch followers
  try {
    const res = await fetch(`https://api.decapi.me/twitch/followcount/${streamer.twitch}`);
    const text = await res.text();
    const n = parseInt(text, 10);
    if (!isNaN(n)) result.twitchFollowers = n;
  } catch {}

  // Resolve YouTube channel ID if not hardcoded
  let channelId = streamer.youtubeChannelId;
  if (!channelId) {
    try {
      const res = await fetch(
        `https://www.googleapis.com/youtube/v3/channels?part=id,statistics&forHandle=${streamer.youtubeHandle}&key=${YOUTUBE_API_KEY}`
      );
      const data = await res.json();
      if (data.items?.[0]) {
        channelId = data.items[0].id;
        result.channelStats = data.items[0].statistics;
      }
    } catch {}
  }

  // YouTube channel stats (if we already have the ID)
  if (channelId && !result.channelStats) {
    try {
      const res = await fetch(
        `https://www.googleapis.com/youtube/v3/channels?part=statistics&id=${channelId}&key=${YOUTUBE_API_KEY}`
      );
      const data = await res.json();
      if (data.items?.[0]?.statistics) result.channelStats = data.items[0].statistics;
    } catch {}
  }

  // YouTube live check
  if (channelId) {
    try {
      const res = await fetch(
        `https://www.googleapis.com/youtube/v3/search?part=snippet&channelId=${channelId}&eventType=live&type=video&key=${YOUTUBE_API_KEY}`
      );
      const data = await res.json();
      if (data.items?.length > 0) {
        const item = data.items[0];
        result.youtubeLive = {
          isLive: true,
          videoId: (item.id as any)?.videoId,
          title: (item.snippet as any)?.title,
        };
      }
    } catch {}
  }

  return result;
}

function LiveBadge({ isLive }: { isLive: boolean }) {
  return isLive ? (
    <span className="text-green-400 flex items-center gap-1 text-xs">
      <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
      Live now
    </span>
  ) : (
    <span className="text-xs text-muted-foreground">Offline</span>
  );
}

function StreamerSection({
  streamer,
  data,
  loading,
}: {
  streamer: typeof STREAMERS[0];
  data: StreamerData | null;
  loading: boolean;
}) {
  return (
    <div className="mb-16">
      {/* Streamer header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="h-px flex-1 bg-border/40" />
        <span className="text-sm font-bold text-muted-foreground uppercase tracking-widest px-3">
          {streamer.displayName}
        </span>
        <div className="h-px flex-1 bg-border/40" />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6 max-w-2xl mx-auto">
        <div className="rounded-xl border border-border bg-card p-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            <i className="ri-twitch-line text-purple-400" />
            <span className="text-xs text-muted-foreground">Twitch Followers</span>
          </div>
          <p className="font-bold text-xl">
            {loading ? "—" : parseFormattedCount(data?.twitchFollowers)}
          </p>
        </div>
        <div className="rounded-xl border border-border bg-card p-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            <i className="ri-youtube-line text-red-500" />
            <span className="text-xs text-muted-foreground">YouTube Subs</span>
          </div>
          <p className="font-bold text-xl">
            {loading ? "—" : parseFormattedCount(data?.channelStats?.subscriberCount)}
          </p>
        </div>
        <div className="rounded-xl border border-border bg-card p-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            <i className="ri-eye-line text-green-500" />
            <span className="text-xs text-muted-foreground">Total Views</span>
          </div>
          <p className="font-bold text-xl">
            {loading ? "—" : parseFormattedCount(data?.channelStats?.viewCount)}
          </p>
        </div>
      </div>

      {/* Embeds */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Twitch */}
        <div className="rounded-xl border border-border bg-card overflow-hidden flex flex-col">
          <div className="flex items-center gap-3 px-5 py-4 border-b border-border/60">
            <i className="ri-twitch-fill text-purple-400 text-xl" />
            <div className="flex-1 min-w-0">
              <h2 className="font-semibold text-sm truncate">{streamer.twitch}</h2>
              <LiveBadge isLive={data?.twitchStream.isLive ?? false} />
            </div>
            <a
              href={`https://www.twitch.tv/${streamer.twitch}`}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 transition-colors"
            >
              Open <i className="ri-external-link-line text-xs" />
            </a>
          </div>
          <div className="relative aspect-video bg-black w-full">
            {data?.twitchStream.isLive ? (
              <iframe
                src={`https://player.twitch.tv/?channel=${streamer.twitch}&parent=${TWITCH_PARENT}&autoplay=false&muted=true`}
                allowFullScreen
                className="absolute inset-0 w-full h-full border-0"
                title={`${streamer.twitch} Twitch Stream`}
              />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-muted-foreground">
                <div className="w-14 h-14 rounded-full bg-white/5 flex items-center justify-center">
                  <i className="ri-twitch-fill text-purple-400/40 text-2xl" />
                </div>
                <p className="text-sm font-medium">{streamer.displayName} is offline</p>
                <p className="text-xs text-muted-foreground/60">Check back later or follow for notifications</p>
              </div>
            )}
          </div>
        </div>

        {/* YouTube */}
        <div className="rounded-xl border border-border bg-card overflow-hidden flex flex-col">
          <div className="flex items-center gap-3 px-5 py-4 border-b border-border/60">
            <i className="ri-youtube-fill text-red-500 text-xl" />
            <div className="flex-1 min-w-0">
              <h2 className="font-semibold text-sm truncate">{streamer.youtubeHandle}</h2>
              <LiveBadge isLive={data?.youtubeLive.isLive ?? false} />
            </div>
            <a
              href={`https://www.youtube.com/@${streamer.youtubeHandle}`}
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
            >
              Channel <i className="ri-external-link-line text-xs" />
            </a>
          </div>
          <div className="relative aspect-video bg-black w-full">
            {data?.youtubeLive.isLive && data.youtubeLive.videoId ? (
              <iframe
                src={`https://www.youtube.com/embed/${data.youtubeLive.videoId}?rel=0`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 w-full h-full border-0"
                title={data.youtubeLive.title || "YouTube Live"}
              />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-muted-foreground">
                <div className="w-14 h-14 rounded-full bg-white/5 flex items-center justify-center">
                  <i className="ri-youtube-fill text-red-500/40 text-2xl" />
                </div>
                <p className="text-sm font-medium">{streamer.displayName} is offline</p>
                <p className="text-xs text-muted-foreground/60">Check back later or subscribe for notifications</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function LivePage() {
  const [streamerData, setStreamerData] = useState<Record<string, StreamerData | null>>(
    Object.fromEntries(STREAMERS.map(s => [s.id, null]))
  );
  const [loading, setLoading] = useState<Record<string, boolean>>(
    Object.fromEntries(STREAMERS.map(s => [s.id, true]))
  );

  useEffect(() => {
    for (const streamer of STREAMERS) {
      fetchStreamerData(streamer).then(data => {
        setStreamerData(prev => ({ ...prev, [streamer.id]: data }));
        setLoading(prev => ({ ...prev, [streamer.id]: false }));
      });
    }
  }, []);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 py-12">
        <div className="mb-10 text-center">
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
              <i className="ri-broadcast-line text-primary text-xl" />
            </div>
            <h1 className="font-bold text-2xl sm:text-3xl">Live Streams</h1>
          </div>
          <p className="text-muted-foreground text-sm max-w-lg mx-auto">
            Catch your favorite AppleSMP streamers live on Twitch and YouTube.
          </p>
        </div>

        {STREAMERS.map(streamer => (
          <StreamerSection
            key={streamer.id}
            streamer={streamer}
            data={streamerData[streamer.id]}
            loading={loading[streamer.id]}
          />
        ))}
      </main>
      <Footer />
    </div>
  );
}
